import React, { useEffect, useRef, useState } from 'react';
import { ButtonWithLoadingIcon } from '../../components/common/ButtonWithLoadingIcon';
import { LoadingContainer } from '../../components/common/LoadingContainer';
import { NotificationManager } from '../../components/common/react-notifications';
import { useUserContext } from '../../context/UserContext';
import { makeFirstLetterCapital } from '../../helpers/utils';
import productAPI from '../../services/API/product';
import { ReviewModalStep1 } from './ReviewModals/ReviewModalStep1';
import { ReviewModalStep2 } from './ReviewModals/ReviewModalStep2';
import { ReviewModalStep3 } from './ReviewModals/ReviewModalStep3';
import { v4 as uuid } from 'uuid';
import userAPI from '../../services/API/user';

const stepsData = [
  {
    title: 'So, tell me about yourself.',
    subTitle:
      'The details below will display with any reviews you write. You can change or edit these preferences in your profile',
    buttons: ['Next'],
  },
  {
    title: 'Asking for a friend...',
    subTitle: `Answering these questions will help your fellow members better understand this item's fit and quality`,
    buttons: ['Next', 'Previous'],
  },
  {
    title: `What'd ya think?`,
    subTitle:
      'Members value and appreciate your honest feedback. Help them decide what size to rent or how to style it!',
    buttons: ['Submit', 'Previous'],
  },
  {
    title: 'Thanks, bestie!',
    subTitle: `Thanks so much for writing a review. Don't worry if you don't see it posted right away, it should be up within 48 hours! Keep being awesome in the meantime.`,
    buttons: ['Done'],
  },
];

export const ReviewModal = ({ productData, onClose, onRefresh }) => {
  const [fittingValue, setFittingValue] = useState('');
  const [fit, setFit] = useState(false);
  const [wearFrequency, setWearFrequency] = useState('');
  const [wearOccassion, setWearOccassion] = useState('');
  const [reviewImage, setReviewImage] = useState(null);
  const [rating, setRating] = useState(0);
  const [reviewText, setReviewText] = useState('');
  const [reviewTitle, setReviewTitle] = useState('');
  const [reviewError, setReviewError] = useState(null);
  const [step, setStep] = useState(1);
  const [isUploadImage, setIsUploadImage] = useState(false);
  const [stepTitle, setStepTitle] = useState(stepsData[step - 1].title);
  const [stepSubTitle, setStepSubTitle] = useState(
    stepsData[step - 1].subTitle
  );
  const [selectedProduct, setSelectedProduct] = useState(productData);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(false);

  const inputRef = useRef(null);

  const lastStep = step === stepsData.length;

  useEffect(() => {
    setStepTitle(stepsData[step - 1].title);
    setStepSubTitle(stepsData[step - 1].subTitle);
  }, [step]);

  useEffect(() => {
    const getProduct = async () => {
      try {
        setLoading(true);
        const productDataRes = await productAPI.getProductById(
          selectedProduct.id ? selectedProduct.id : selectedProduct.product_id
        );
        const userId = localStorage.getItem('userId');
        const userRes = await userAPI.getUserById(userId);
        setUserData(userRes);
        setSelectedProduct(productDataRes);
        setLoading(false);
      } catch {
        setLoading(false);
      }
    };
    getProduct();
  }, []);

  const { user, wearOccassions, wearFrequencies } = useUserContext();
  const [submittingReview, setSubmittingReview] = useState(false);

  const handleSubmitReview = (reviewData) => {
    let data = { product_id: selectedProduct.id, user_id: user.id };
    if (step === 2) {
      setWearFrequency(reviewData.wear_frequency);
      setWearOccassion(reviewData.wear_occassion);
      setFit(reviewData.fit);
      setFittingValue(reviewData.fit_review);
      if (
        reviewData.wear_frequency !== '' &&
        reviewData.wear_occassion !== ''
      ) {
        setStep(step + 1);
      }
    }
    if (step === 3) {
      setRating(reviewData.rating ? reviewData.rating : 0);
      setReviewText(reviewData.review_text);
      setReviewTitle(reviewData.review_title);
      if (reviewData?.review_text?.length <= 500) {
        setReviewError(null);
        data = {
          ...data,
          wear_occassion: wearOccassion,
          wear_frequency: wearFrequency,
          fit,
          fit_review: fittingValue,
          review_stars: reviewData.rating ? reviewData.rating : 0,
          review_text: reviewData.review_text,
          review_title: reviewData.review_title,
          img_url: reviewImage,
        };
        setSubmittingReview(true);
        productAPI
          .addReview(data)
          .then((response) => {
            setStep(step + 1);
            NotificationManager.success({
              message: 'Review Added',
            });
          })
          .catch((error) => {
            NotificationManager.error({
              message: 'Something went wrong, please try again',
            });
          })
          .finally(() => {
            setSubmittingReview(true);
          });

        setReviewError(null);
        setSubmittingReview(false);
      } else {
        setReviewError('you can enter maximum 500 character');
      }
    }
    if (step === 4) {
      onClose();
      if (onRefresh) {
        onRefresh();
      }
    }
  };
  const saveValue = (reviewData) => {
    if (reviewData) {
      if (step === 2) {
        setWearFrequency(reviewData.wear_frequency);
        setWearOccassion(reviewData.wear_occassion);
        setFit(reviewData.fit);
        setFittingValue(reviewData.fit_review);
      }
      if (step === 3) {
        setRating(reviewData.rating ? reviewData.rating : 0);
        setReviewText(reviewData.review_text);
        setReviewTitle(reviewData.review_title);
      }
    }
  };

  const renderTitleValuePair = (title, value) => {
    return (
      <div className={`basic-detail-item py-0 mb-1`}>
        <div
          className='basic-detail-label'
          style={{ color: '#2E2B2B', fontSize: 15, fontFamily: 'Brown' }}
        >{`${title}: `}</div>
        <div
          className={`basic-detail-data`}
          style={{
            fontFamily: 'Brown',
            fontWeight: 400,
            fontSize: 15,
            color: '#2E2B2B',
          }}
        >
          {makeFirstLetterCapital(value) || ''}
        </div>
      </div>
    );
  };

  const renderHeader = () => {
    return (
      <div
        className='d-flex align-items-center'
        style={{
          backgroundColor: '#F9E4D7',
          width: 'auto',
          marginLeft: '2.5rem',
          marginRight: '2.5rem',
          color: '#2E2B2B',
        }}
      >
        <div
          className={'mr-3'}
          style={{ height: 140, width: 110, border: 'none' }}
        >
          <img
            src={productData.img_url}
            alt={selectedProduct.title}
            style={{ height: '100%', width: '100%', border: ' none' }}
            loading={'lazy'}
          />
        </div>
        <div>
          <span style={{ fontFamily: 'Brown', fontSize: 20 }}>
            {selectedProduct.brand || '--'}
          </span>
          <br />
          <span className='mb-2' style={{ fontFamily: 'Brown', fontSize: 20 }}>
            {selectedProduct.title || selectedProduct.product_title || ''}
          </span>
          <div style={{ marginTop: 18 }}>
            {renderTitleValuePair('Size', selectedProduct.size)}
            {renderTitleValuePair('Color', selectedProduct.color)}
          </div>
        </div>
      </div>
    );
  };

  const renderTitleSubtitle = () => {
    return (
      <div
        className='review-title-subtitle'
        style={{
          paddingTop: lastStep ? 10 : 20,
          marginTop: lastStep ? 0 : 25,
          marginBottom: lastStep ? '2.25rem' : 0,
        }}
      >
        <h5
          className='review-title'
          style={{ fontFamily: 'Brown Bold', fontSize: 25, marginBottom: 15 }}
        >
          {stepTitle}
        </h5>
        <span className='review-subtitle'>{stepSubTitle}</span>
      </div>
    );
  };

  const onPressButton = (data) => {
    switch (step) {
      case 1:
        setStep(step + 1);
        break;
      case 2:
        handleSubmitReview(data);
        break;
      case 3:
        handleSubmitReview(data);
        break;
      case 4:
        handleSubmitReview();
        break;
      default:
        break;
    }
  };

  const renderButton = () => {
    return (
      <div
        className={lastStep ? 'review-fixed-button' : 'review-button'}
        style={lastStep ? { position: 'relative' } : {}}
      >
        <ButtonWithLoadingIcon
          style={{
            alignSelf: 'flex-start',
            width: '100%',
            marginBottom: 10,
          }}
          color={'primary'}
          onClick={() => onPressButton()}
        >
          Done
        </ButtonWithLoadingIcon>
      </div>
    );
  };

  const generatePreviewData = (file) => {
    const fr = new FileReader();
    return new Promise((resolve, reject) => {
      fr.addEventListener('load', (e) => {
        let res = fr.result;
        resolve({ uri: res, file: file, id: uuid() });
      });
      fr.addEventListener('error', (e) => {
        reject();
      });
      fr.readAsDataURL(file);
    });
  };

  const onChangeFile = ({ target }) => {
    if (target.files.length > 0) {
      setIsUploadImage(true);

      const res = generatePreviewData(target.files[0]);
      res
        .then((img) => {
          const imgData = { ...img, img_url: img.uri, progress: 0 };
          uploadImage(imgData);
        })
        .catch((e) => {
          setIsUploadImage(false);
        });
    } else {
      setIsUploadImage(false);
    }
  };

  const uploadImage = async (newImage) => {
    productAPI
      .uploadImage(newImage)
      .then(({ img_url }) => {
        setReviewImage(img_url);
        setIsUploadImage(false);
      })
      .catch((err) => {
        console.log('Error', err);
      });
  };

  return (
    <div>
      {lastStep ? null : renderHeader()}
      {renderTitleSubtitle()}
      {loading ? (
        <LoadingContainer style={{ backgroundColor: 'transparent' }} />
      ) : (
        <div>
          {step === 1 ? (
            <ReviewModalStep1
              productData={selectedProduct}
              userData={userData}
              onPressButton={() => onPressButton()}
            />
          ) : step === 2 ? (
            <ReviewModalStep2
              productData={selectedProduct}
              onPrevious={(value) => {
                setStep(step - 1);
                saveValue(value);
              }}
              fitValue={fit}
              fitReviewValue={fittingValue}
              wearOccassionValue={wearOccassion}
              wearFrequencyValue={wearFrequency}
              onPressButton={onPressButton}
              wearOccassionsData={wearOccassions}
              wearFrequenciesData={wearFrequencies}
            />
          ) : step === 3 ? (
            <ReviewModalStep3
              productData={selectedProduct}
              onFileChange={onChangeFile}
              reviewImage={reviewImage}
              reviewTextValue={reviewText}
              reviewTitleValue={reviewTitle}
              ratingValue={rating}
              inputRef={inputRef}
              reviewError={reviewError}
              onPrevious={(value) => {
                setStep(step - 1);
                saveValue(value);
              }}
              submittingReview={submittingReview}
              isUploadImage={isUploadImage}
              onPressButton={onPressButton}
            />
          ) : null}
        </div>
      )}
      {step === 4 ? renderButton() : null}
    </div>
  );
};
